import { Severity } from '../../enums/Severity';
import { InvalidUrlError } from '../../errors/errors';
import { BaseGQLService } from '../../services/BaseGQLService';
import { InfinitePaginationResult } from '../../types/pagination';
import { ModelType } from '../../utils/GQLIdEncoder';
import { IssueMapper } from '../mappers/IssueMapper';
import { Issue } from '../models/Issue';

export const get_issue_query = (cursor: string | null, hasSeveritiesFilter: boolean) => {
  return `
      query (
        $perPage: Int,
        $modelId: ID!,
        $withExceptions: Boolean!,
        ${cursor ? '$cursor: String,' : ''}
        ${hasSeveritiesFilter ? '$severities: [DisplaySeverity!],' : ''}
      ) {
        model: node(id: $modelId) {
          ... on Project {
            vulnerabilities(
              first: $perPage,
              withExceptions: $withExceptions,
              ${cursor ? `after: $cursor,` : ''}
              ${hasSeveritiesFilter ? `severities: $severities,` : ''}
            ) {
              pageInfo {
                hasNextPage
                endCursor
              }
              totalCount
              edges {
                node {
                  id
                  number
                  shortExplanation
                  explanation
                  consequences
                  title
                  access
                  expertise
                  resources
                  kbLink
                  numberOfSlots
                  severity
                }
              }
            }
          }
        }
      }
      `;
};

export class IssuesRepository extends BaseGQLService {
  private readonly mapper = new IssueMapper();

  /**
   * Fetch list through infinite pagination approach where we only fetch data after cursor.
   */
  async getList(
    modelId: number,
    cursor: string | null,
    perPage = 10,
    severities: Severity[] = [],
    withExceptions = false
  ): Promise<InfinitePaginationResult<Issue>> {
    const hasSeveritiesFilter = severities.length > 0;

    return this.query(
      get_issue_query(cursor, hasSeveritiesFilter),
      {
        perPage,
        withExceptions,
        modelId: this.gqlIdEncoder.encode(ModelType.Project, modelId),
        cursor,
        severities: severities.flatMap((s) => this.mapSeverityToGQLValue(s)),
      },
      (data) => {
        if (!data.model) {
          throw new InvalidUrlError(`Invalid request: could not find Project ${modelId}`);
        }
        const issuesData = data.model.vulnerabilities;
        return {
          data: issuesData.edges.map((p: any) => this.mapper.mapToDomain(p.node)),
          cursor: issuesData.pageInfo.endCursor,
          hasNextPage: issuesData.pageInfo.hasNextPage,
          totalItems: issuesData.totalCount,
        };
      }
    );
  }
  private mapSeverityToGQLValue(severity: Severity): string[] {
    switch (severity) {
      case Severity.HIGH:
        return ['HIGH'];
      case Severity.MED:
        return ['MEDIUM'];
      case Severity.LOW:
        return ['LOW'];
      case Severity.PASSED:
        return ['PASSED'];
      case Severity.NA:
        return ['NA'];
      case Severity.FILTERED:
        return ['FILTERED'];
      case Severity.DISABLED:
        return ['DISABLED'];
      case Severity.NO_WARNING:
        return ['PASSED', 'NA', 'FILTERED', 'DISABLED'];
      default:
        return [severity];
    }
  }
}
